import {
  Card,
  Divider,
  InlineFormattedNumber,
  InstrumentCompositionEnum,
  Side,
  SmartSummary,
  SymbolDisplay,
  SymbolDisplaySize,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  UnifiedLiquidityEnum,
  getFilledNotional,
  getPricingReference,
  getSymbolsAcrossMarkets,
  getTotalNotional,
  isMultileg,
  useCurrency,
  useSecuritiesContext,
  useSecurity,
  type CardProps,
  type CustomerOrder,
  type Order,
} from '@talos/kyoko';
import { useMemo } from 'react';

export interface OrderCardProps extends CardProps {
  order: Order;
  customerOrder?: CustomerOrder;
  showExpandedDetails?: boolean;
}

export function OrderCard({ order, customerOrder, showExpandedDetails, ...props }: OrderCardProps) {
  const security = useSecurity(order.Symbol);
  const currency = useCurrency(order.Currency);
  const amountCurrency = useCurrency(order.AmountCurrency);
  const { securitiesBySymbol } = useSecuritiesContext();

  const legsData = useMemo(() => {
    // nullcheck
    const legSummary = order.LegSummary ?? [];

    // LegSummary includes parentLeg if it's a unified liquidity multileg with a native pair (BTC-EUR:USD and BTC-EUR)
    // Filter out the parentLeg to not include it in the legsData
    return legSummary.filter(
      leg => securitiesBySymbol.get(leg.Symbol)?.Composition !== InstrumentCompositionEnum.Synthetic
    );
  }, [order.LegSummary, securitiesBySymbol]);

  const totalNotional = getTotalNotional(order, security);
  const customerTotalNotional = customerOrder == null ? undefined : getTotalNotional(customerOrder, security);
  const filledNotional = getFilledNotional(order, security);
  const notionalCurrencyInfo = useCurrency(filledNotional.currency);
  const notionalIncrement = notionalCurrencyInfo?.DefaultIncrement ?? amountCurrency?.DefaultIncrement;

  const underlyingSymbols =
    order.unifiedLiquidity === UnifiedLiquidityEnum.Enabled ? getSymbolsAcrossMarkets(order) : [];

  const placedPriceAmount = getPricingReference(order);

  return (
    <Card title="Order" {...props}>
      <Table w="100%">
        {customerOrder != null && (
          <Thead>
            <Tr>
              <Th align="left" colSpan={customerOrder == null ? 2 : 1} pl={0} pb="spacing8">
                <Card.Subtitle mb={0}>Order</Card.Subtitle>
              </Th>
              <Th align="right" w="33%" pb="spacing8">
                Customer
              </Th>
              <Th align="right" w="33%" pb="spacing8">
                Hedge
              </Th>
            </Tr>
          </Thead>
        )}
        <Tbody>
          <Tr>
            <Th>Symbol</Th>
            {customerOrder != null && (
              <Td align="right">
                <SymbolDisplay
                  symbol={customerOrder.Symbol}
                  underlyingSymbols={underlyingSymbols}
                  showDescription={false}
                  size={SymbolDisplaySize.Small}
                  p={0}
                  justifyContent="flex-end"
                  data-testid="customer-order-symbol"
                />
              </Td>
            )}
            <Td align="right" width="100%">
              <SymbolDisplay
                symbol={order.Symbol}
                underlyingSymbols={underlyingSymbols}
                showDescription={false}
                showJSON={true}
                size={SymbolDisplaySize.Small}
                p={0}
                justifyContent="flex-end"
                data-testid="order-symbol"
              />
            </Td>
          </Tr>
          {showExpandedDetails &&
            legsData?.map((leg, i) => (
              <Tr key={leg.Symbol}>
                <Th>Leg {i + 1}</Th>
                {/* Add empty cell to keep cols aligned. CustomerOrder has three cols.  */}
                {customerOrder != null && <Td align="right" />}
                <Td align="right" width="100%">
                  <SymbolDisplay
                    data-testid={`ord-details-order-card-leg-${i + 1}`}
                    symbol={leg.Symbol}
                    showDescription={false}
                    showJSON={true}
                    size={SymbolDisplaySize.Small}
                    p={0}
                    justifyContent="flex-end"
                  />
                </Td>
              </Tr>
            ))}
          <Tr>
            <Th>Side</Th>
            {customerOrder != null && (
              <Td align="right">
                <Side side={order.Side} data-testid="customer-order-side">
                  {order.Side}
                </Side>
              </Td>
            )}
            <Td align="right">
              <Side side={order.Side} data-testid="order-side">
                {order.Side}
              </Side>
            </Td>
          </Tr>
          {isMultileg(security) && (
            <Tr>
              <Th>Product</Th>
              {customerOrder != null && <Td align="right" />}
              <Td align="right" w="33%" pb="spacing8">
                {security?.MultilegDetails?.SyntheticProductType}
              </Td>
            </Tr>
          )}
          <Tr>
            <Th whiteSpace="nowrap">Order Qty</Th>
            {customerOrder != null && (
              <Td align="right">
                <SmartSummary
                  entity={customerOrder}
                  type="order"
                  showPrice={false}
                  showSide={false}
                  data-testid="customer-order-qty"
                />
              </Td>
            )}
            <Td align="right">
              <SmartSummary entity={order} type="order" showPrice={false} showSide={false} data-testid="order-qty" />
            </Td>
          </Tr>
          <Tr>
            <Th>Price</Th>
            {customerOrder != null && (
              <Td align="right">
                <SmartSummary
                  entity={customerOrder}
                  type="order"
                  showQty={false}
                  showSide={false}
                  data-testid="customer-order-price"
                />
              </Td>
            )}
            <Td align="right">
              <SmartSummary entity={order} type="order" showQty={false} showSide={false} data-testid="order-price" />
            </Td>
          </Tr>

          {placedPriceAmount && (
            <Tr>
              <Th>Reference Price</Th>
              <Td align="right">
                <InlineFormattedNumber number={placedPriceAmount.value} currency={placedPriceAmount.currency} />
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
      {showExpandedDetails && (
        <>
          <Divider my="spacing12" />
          <Table w="100%">
            <Tbody>
              <Tr>
                <Th>Total Notional</Th>
                {customerTotalNotional != null && (
                  <Td align="right" w="33%">
                    <InlineFormattedNumber
                      number={customerTotalNotional.value}
                      currency={customerTotalNotional.currency}
                      increment={notionalIncrement}
                    />
                  </Td>
                )}
                <Td align="right" w={customerTotalNotional == null ? undefined : '33%'}>
                  <InlineFormattedNumber
                    number={totalNotional.value}
                    currency={totalNotional.currency}
                    increment={notionalIncrement}
                  />
                </Td>
              </Tr>
              <Tr>
                <Th>Expected Fill Price</Th>
                <Td align="right" colSpan={customerOrder == null ? 1 : 2}>
                  <InlineFormattedNumber
                    number={order.ExpectedFillPrice}
                    currency={order.AmountCurrency}
                    increment={amountCurrency?.DefaultIncrement}
                    align="right"
                  />
                </Td>
              </Tr>
              <Tr>
                <Th>Expected Fill Qty</Th>
                <Td align="right" colSpan={customerOrder == null ? 1 : 2}>
                  <InlineFormattedNumber
                    number={order.ExpectedFillQty}
                    currency={order.Currency}
                    increment={currency?.DefaultIncrement}
                    align="right"
                  />
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </>
      )}
    </Card>
  );
}
